import * as THREE from 'three';

/**
 * `?fill`: how much of the screen each object shades, independent of the GPU and its timers
 * (works in background tabs, where timer queries are throttled into noise). Every material is
 * swapped for a counting twin — same vertex stage, discards and depth state, but each fragment adds
 * 1/255 — and the frame is drawn at half resolution in its real order. Values are in "screens":
 * 1.0 = every pixel shaded once.
 *
 * - total / opaque: fragments that pass the depth test in the real draw order (what the GPU shades).
 * - per object: raw coverage (no depth test) and visible coverage (tested against the full frame's
 *   depth); for an opaque object raw − visible is the waste if it is drawn before its occluders.
 */
export function measureFill(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera) {
  const W = Math.max(1, Math.round(renderer.domElement.width / 2)), H = Math.max(1, Math.round(renderer.domElement.height / 2));
  const rt = new THREE.WebGLRenderTarget(W, H, { depthBuffer: true }), buf = new Uint8Array(W * H * 4);
  const twins = new Map<THREE.Material, THREE.Material>();
  const twin = (m: THREE.Material) => {
    let c = twins.get(m);
    if (c) return c;
    c = m.clone();
    Object.assign(c, { blending: THREE.CustomBlending, blendSrc: THREE.OneFactor, blendDst: THREE.OneFactor, blendEquation: THREE.AddEquation, depthWrite: m.depthWrite, depthTest: m.depthTest, transparent: m.transparent });
    // wrap the real main: early returns still land in the counter, discards still discard
    const count = (fs: string) => fs.replace(/void\s+main\s*\(\s*\)/, 'void fillMain()') + '\nvoid main() { fillMain(); gl_FragColor = vec4(1.0 / 255.0); }';
    if ((m as THREE.ShaderMaterial).isShaderMaterial) {
      const s = c as THREE.ShaderMaterial; s.fragmentShader = count(s.fragmentShader); s.uniforms = (m as THREE.ShaderMaterial).uniforms;
    } else {
      const prev = m.onBeforeCompile.bind(m), key = m.customProgramCacheKey();
      c.onBeforeCompile = (sh, r) => { prev(sh, r); sh.fragmentShader = count(sh.fragmentShader); };
      c.customProgramCacheKey = () => 'fill-' + key + m.uuid;
    }
    twins.set(m, c);
    return c;
  };
  const items: THREE.Mesh[] = [];
  scene.traverseVisible((o) => { const m = o as THREE.Mesh; if ((m.isMesh || (o as THREE.Points).isPoints) && m.material && !Array.isArray(m.material)) items.push(m); });
  const real = items.map((o) => o.material as THREE.Material);
  const sum = () => { renderer.readRenderTargetPixels(rt, 0, 0, W, H, buf); let s = 0; for (let i = 0; i < buf.length; i += 4) s += buf[i]; return s / (W * H); };
  const bg = scene.background, shadows = renderer.shadowMap.autoUpdate, prevTarget = renderer.getRenderTarget(), clear = new THREE.Color(), alpha = renderer.getClearAlpha();
  renderer.getClearColor(clear);
  scene.background = null; renderer.shadowMap.autoUpdate = false; renderer.shadowMap.needsUpdate = false;
  renderer.setRenderTarget(rt); renderer.setClearColor(0, 0);
  const pass = (only?: (m: THREE.Material) => boolean) => {
    items.forEach((o, i) => { o.material = twin(real[i]); o.visible = !only || only(real[i]); });
    renderer.clear(); renderer.render(scene, camera);
    return sum();
  };
  const total = pass(), opaque = pass((m) => !m.transparent);
  // the real frame's depth, then each object alone against it (visible) and without it (raw)
  items.forEach((o, i) => { o.material = real[i]; o.visible = true; });
  renderer.clear(); renderer.render(scene, camera);
  items.forEach((o) => (o.visible = false));
  const rows = items.map((o, i) => {
    const t = twin(real[i]); o.material = t; o.visible = true;
    renderer.clear(true, false, false); renderer.render(scene, camera); const visible = sum();
    const depthTest = t.depthTest; t.depthTest = false; renderer.clear(true, false, false); renderer.render(scene, camera); const raw = sum(); t.depthTest = depthTest;
    o.visible = false; o.material = real[i];
    return { part: `${o.name || '·'} : ${real[i].name || real[i].type}`, kind: real[i].transparent ? 'T' : 'O', order: o.renderOrder, raw: +raw.toFixed(3), visible: +visible.toFixed(3) };
  });
  items.forEach((o, i) => { o.material = real[i]; o.visible = true; });
  scene.background = bg; renderer.shadowMap.autoUpdate = shadows; renderer.setRenderTarget(prevTarget); renderer.setClearColor(clear, alpha);
  twins.forEach((m) => m.dispose()); rt.dispose();
  rows.sort((a, b) => b.raw - a.raw);
  return { total: +total.toFixed(3), opaque: +opaque.toFixed(3), rows };
}
